import {
  MockedOrganization,
  AdmissionState,
  ActivityState
} from '../../../services/organizations/organization.model';
import onlyOneOrganization from './one.json';
import multipleOrganizations from './multiple.json';
import members from '../members/members.json';
import { BehaviorSubject } from 'rxjs';
import { base64ToBlob, fileToBase64, createFileFromStorage } from '../../file-storage.utils';

export type OrganizationMockMode = 'empty' | 'one' | 'multiple';

const STORAGE_KEY = 'htx-mock-organizations-mode';
const ADDITIONAL_STORAGE_KEY = 'htx-mock-additional-organizations-data';
const STATE_CHANGES_STORAGE_KEY = 'htx-mock-organizations-state-changes';
const MEMBER_UPDATES_STORAGE_KEY = 'htx-mock-member-updates';
const ADDITIONAL_MEMBERS_STORAGE_KEY = 'htx-mock-additional-members';

let additionalOrganizations: MockedOrganization[] = [];
let organizationChanges: Map<string, any> = new Map();
let memberUpdates: Map<string, any> = new Map();
let additionalMembers: any[] = [];

function loadAdditionalOrganizations(): void {
  const stored = localStorage.getItem(ADDITIONAL_STORAGE_KEY);
  if (stored) {
    const parsed = JSON.parse(stored);
    additionalOrganizations = parsed.map((p: any) => ({
      ...p,
      document: p.document && p.document.fileName ? createFileFromStorage(p.document) : null
    }));
  }
}

function loadStateChanges(): void {
  const stored = localStorage.getItem(STATE_CHANGES_STORAGE_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      organizationChanges = new Map(Object.entries(parsed));
    } catch (e) {
      console.error('Failed to load organization changes:', e);
      organizationChanges = new Map();
    }
  }
}

function loadMemberUpdates(): void {
  const stored = localStorage.getItem(MEMBER_UPDATES_STORAGE_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      memberUpdates = new Map(Object.entries(parsed));
    } catch (e) {
      console.error('Failed to load member updates:', e);
      memberUpdates = new Map();
    }
  }
}

function saveStateChanges(): void {
  const obj = Object.fromEntries(organizationChanges);
  localStorage.setItem(STATE_CHANGES_STORAGE_KEY, JSON.stringify(obj));
}

function saveMemberUpdates(): void {
  const obj = Object.fromEntries(memberUpdates);
  localStorage.setItem(MEMBER_UPDATES_STORAGE_KEY, JSON.stringify(obj));
}

function loadAdditionalMembers(): void {
  const stored = localStorage.getItem(ADDITIONAL_MEMBERS_STORAGE_KEY);
  if (stored) {
    try {
      additionalMembers = JSON.parse(stored);
    } catch (e) {
      console.error('Failed to load additional members:', e);
      additionalMembers = [];
    }
  }
}

function saveAdditionalMembers(): void {
  localStorage.setItem(ADDITIONAL_MEMBERS_STORAGE_KEY, JSON.stringify(additionalMembers));
}

function getInitialMode(): OrganizationMockMode {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'empty' || stored === 'one' || stored === 'multiple') {
    return stored;
  }
  return 'multiple';
}
const currentMode$ = new BehaviorSubject<OrganizationMockMode>(getInitialMode());

// Initialize on load
loadAdditionalOrganizations();
loadStateChanges();
loadMemberUpdates();
loadAdditionalMembers();

export function loadEmptyOrganizations(): MockedOrganization[] {
  return [];
}

export function loadOnlyOneOrganization(): MockedOrganization[] {
  return onlyOneOrganization.map((p) => ({
    ...p,
    document: createFileFromStorage(p.document)
  })) as MockedOrganization[];
}

export function loadMultipleOrganizations(): MockedOrganization[] {
  return multipleOrganizations.map((p) => ({
    ...p,
    document: createFileFromStorage(p.document)
  })) as MockedOrganization[];
}

export function setOrganizationMockMode(mode: OrganizationMockMode): void {
  localStorage.setItem(STORAGE_KEY, mode);
  currentMode$.next(mode);
}

export function getOrganizationMockMode$() {
  return currentMode$.asObservable();
}

export function getCurrentOrganizationMockMode(): OrganizationMockMode {
  return currentMode$.value;
}

export function getOrganizations(): MockedOrganization[] {
  const mode = currentMode$.value;
  let organizations: MockedOrganization[];

  switch (mode) {
    case 'empty':
      organizations = loadEmptyOrganizations();
      break;
    case 'one':
      organizations = loadOnlyOneOrganization();
      break;
    case 'multiple':
      organizations = loadMultipleOrganizations();
      break;
    default:
      organizations = loadMultipleOrganizations();
  }

  loadAdditionalOrganizations();

  // Apply state changes to organizations
  organizations = organizations.map((p) => {
    const changes = organizationChanges.get(p.id);
    if (changes) {
      return {
        ...p,
        ...changes
      };
    }
    return p;
  });

  return [...organizations, ...additionalOrganizations];
}

export function addOrganization(organization: MockedOrganization): void {
  additionalOrganizations.push(organization);
  saveAdditionalOrganizations();
}

async function saveAdditionalOrganizations(): Promise<void> {
  // Convert Files to base64 for storage
  const storageFormat = await Promise.all(
    additionalOrganizations.map(async (p) => ({
      ...p,
      document: p.document
        ? {
            fileName: p.document.name,
            contentType: p.document.type,
            data: await fileToBase64(p.document),
            size: p.document.size
          }
        : null
    }))
  );
  localStorage.setItem(ADDITIONAL_STORAGE_KEY, JSON.stringify(storageFormat));
}

export function clearAdditionalOrganizations(): void {
  additionalOrganizations = [];
  localStorage.removeItem(ADDITIONAL_STORAGE_KEY);
}

export function clearStateChanges(): void {
  organizationChanges = new Map();
  localStorage.removeItem(STATE_CHANGES_STORAGE_KEY);
}

export function hasStateChanges(): boolean {
  loadStateChanges();
  return organizationChanges.size > 0;
}

export function hasAdditionalOrganizations(): boolean {
  loadAdditionalOrganizations();
  return additionalOrganizations.length > 0;
}

export function updateOrganizationStateChange(
  orgId: string,
  admissionState?: AdmissionState,
  activityState?: ActivityState
): void {
  loadStateChanges();

  const changes = organizationChanges.get(orgId) || {};
  if (admissionState !== undefined) {
    changes.admissionState = admissionState;
  }
  if (activityState !== undefined) {
    changes.activityState = activityState;
  }

  organizationChanges.set(orgId, changes);
  saveStateChanges();
}

export function updateOrganizationData(orgId: string, updates: Partial<MockedOrganization>): void {
  loadStateChanges();

  const existing = organizationChanges.get(orgId) || {};
  organizationChanges.set(orgId, { ...existing, ...updates });
  saveStateChanges();
}

export function getMembers(orgId?: string): any[] {
  if (orgId) {
    // Get the organization to check the representative
    const allOrganizations = getOrganizations();
    const organization = allOrganizations.find((p) => p.id === orgId);

    // Get members for this org
    const orgMembers = members
      .filter((m) => m.organizationId === orgId)
      .map((m) => ({
        ...m,
        organizationId: orgId,
        roles: m.roles || ['ORGANIZATION_USER']
      }));

    // Handle representative
    if (organization && organization.representative) {
      const rep = organization.representative;
      const repMember = orgMembers.find(
        (m) => m.firstName === rep.firstName && m.lastName === rep.lastName
      );

      if (repMember) {
        // Merge representative roles with existing member roles (case-insensitive dedup)
        const repRoles = (rep.roles || ['ORGANIZATION_REPRESENTATIVE', 'ORGANIZATION_ADMIN']).map(
          (r: string) => r.toLowerCase()
        );
        const existingRoles = repMember.roles.map((r: string) => r.toLowerCase());
        const mergedRoles = [...new Set([...existingRoles, ...repRoles])];
        repMember.roles = mergedRoles;
      } else {
        // Add representative as a new member if not found in the list
        orgMembers.unshift({
          id: `member-rep-${orgId}`,
          organizationId: orgId,
          firstName: rep.firstName,
          lastName: rep.lastName,
          title: rep.title || '',
          position: rep.position || 'Representative',
          email: rep.email || '',
          phoneNumber: rep.phoneNumber || '',
          roles: rep.roles || ['ORGANIZATION_REPRESENTATIVE', 'ORGANIZATION_ADMIN']
        } as any);
      }
    }

    // Append additional members for this org
    const extra = additionalMembers
      .filter((m) => m.organizationId === orgId)
      .map((m) => ({ ...m, roles: m.roles || ['ORGANIZATION_USER'], activityState: m.activityState || 'ACTIVE' }));
    return [...orgMembers, ...extra];
  }
  return [
    ...members.map((m) => ({
      ...m,
      roles: m.roles || ['ORGANIZATION_USER'],
      activityState: (m as any).activityState || 'ACTIVE'
    })),
    ...additionalMembers.map((m) => ({ ...m, roles: m.roles || ['ORGANIZATION_USER'], activityState: m.activityState || 'ACTIVE' }))
  ];
}

export function getMemberDetails(memberId: string): any {
  // Search across all organizations for this member
  const allOrganizations = getOrganizations();

  for (const org of allOrganizations) {
    const orgMembers = getMembers(org.id);
    const member = orgMembers.find((m) => m.id === memberId);
    if (member) {
      // Get stored updates for this member
      const storedUpdates = memberUpdates.get(memberId);

      const memberDetails = {
        ...member,
        organizationId: org.id,
        email:
          member.email ||
          `${member.firstName.toLowerCase()}.${member.lastName.toLowerCase()}@example.com`,
        roles: member.roles || ['ORGANIZATION_USER'],
        activityState: member.activityState || 'ACTIVE'
      };

      // Apply stored updates if available
      if (storedUpdates) {
        return {
          ...memberDetails,
          ...storedUpdates
        };
      }

      return memberDetails;
    }
  }

  return null;
}

export function getMemberByIamId(iamId: string): any {
  const allOrganizations = getOrganizations();
  for (const org of allOrganizations) {
    const orgMembers = getMembers(org.id);
    const member = orgMembers.find((m: any) => m.iamId === iamId);
    if (member) {
      return getMemberDetails(member.id);
    }
  }
  // Fallback: search in flat members list
  const flatMember = members.find((m: any) => m.iamId === iamId);
  if (flatMember) {
    return getMemberDetails((flatMember as any).id);
  }
  return null;
}

export function updateMemberData(memberId: string, memberData: any): any {
  // Get the current member data
  const updatedMember = getMemberDetails(memberId);

  if (updatedMember) {
    // Merge new data with existing updates
    const merged = {
      ...updatedMember,
      ...memberData
    };

    // Store the updates in the map
    memberUpdates.set(memberId, merged);

    // Persist to localStorage
    saveMemberUpdates();

    return merged;
  }

  return null;
}

export function hasAdditionalMembers(): boolean {
  return additionalMembers.length > 0;
}

export function clearAdditionalMembers(): void {
  additionalMembers = [];
  localStorage.removeItem(ADDITIONAL_MEMBERS_STORAGE_KEY);
}

export function addMember(memberData: any): any {
  const newMember = {
    id: `member-${crypto.randomUUID()}`,
    activityState: 'ACTIVE',
    ...memberData
  };
  additionalMembers.push(newMember);
  saveAdditionalMembers();
  return newMember;
}

export function getOrganizationDocument(orgId: string): Blob | null {
  const allOrganizations = getOrganizations();
  const organization = allOrganizations.find((p) => p.id === orgId);
  if (organization && organization.document) {
    return organization.document;
  }
  return null;
}
